import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Leaflet's default marker icon references relative image paths that break
// once bundled by webpack/CRA — re-point them at the actual bundled assets.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const DEFAULT_CENTER = [15.5007, 32.5599]; // Khartoum — reasonable starting view, not a value ever submitted
const SEARCH_DEBOUNCE_MS = 700; // Nominatim's usage policy asks for ~1 req/sec max; a typing pause this
                                  // long keeps well under that even for fast typists (words naturally
                                  // pause longer than this), without needing a dedicated backend proxy.
const AUTO_OPEN_MIN_CHARS = 3;   // don't pop the map open for one keystroke — wait for something searchable

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick([e.latlng.lat, e.latlng.lng]) });
  return null;
};

// Leaflet's <MapContainer center> only sets the *initial* view — it doesn't
// react to prop changes after mount. This re-centers the map imperatively
// whenever `position` changes for any reason: click, drag, or a text search.
const FlyToPosition = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, Math.max(map.getZoom(), 14), { duration: 0.8 });
  }, [position, map]);
  return null;
};

/* ═══════════════════════════════════════════════════════════
   LocationPickerModal — purely presentational now: the address text,
   map position, and all geocoding state live in LocationField (the
   parent) so the same keystrokes that opened this modal keep driving it —
   there's no second, separately-typed-into input to fall out of sync.
═══════════════════════════════════════════════════════════ */
const LocationPickerModal = ({
  title, position, address, geocoding, searching,
  onAddressChange, onAddressKeyDown, onMapClick, onMarkerDrag, onLocateMe, locating,
  onConfirm, onClose,
}) => (
  <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center" dir="rtl">
    <div className="absolute inset-0 bg-black/50" onClick={onClose} />
    <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-[28px] shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
      <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
        <h2 className="font-bold text-gray-800">{title || 'حدد الموقع على الخريطة'}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
      </div>

      <div className="relative flex-1" style={{ minHeight: 320 }}>
        <MapContainer center={position || DEFAULT_CENTER} zoom={position ? 15 : 12} style={{ height: '100%', width: '100%', minHeight: 320 }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={onMapClick} />
          <FlyToPosition position={position} />
          {position && <Marker position={position} draggable eventHandlers={{ dragend: onMarkerDrag }} />}
        </MapContainer>

        <button
          type="button" onClick={onLocateMe} disabled={locating}
          className="absolute bottom-3 left-3 z-[1000] bg-white shadow-md rounded-full w-11 h-11 flex items-center justify-center text-[#1E40AF] disabled:opacity-50"
          title="موقعي الحالي"
        >
          {locating ? (
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          ) : (
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          )}
        </button>
      </div>

      <div className="p-4 border-t flex-shrink-0 space-y-3">
        <p className="text-xs text-gray-500">اكتب اسم المكان ليتحرك إليه المؤشر، أو اضغط على الخريطة مباشرة، أو اسحب العلامة لضبطها بدقة</p>
        <div className="relative">
          <input
            autoFocus
            className="w-full border-2 border-gray-200 focus:border-[#1E3A8A] rounded-xl px-4 py-2.5 pl-10 text-sm outline-none"
            placeholder={geocoding ? 'جارٍ تحديد العنوان...' : 'مثال: بورتسودان'}
            value={address}
            onChange={onAddressChange}
            onKeyDown={onAddressKeyDown}
          />
          {(searching || geocoding) && (
            <svg className="animate-spin w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          )}
        </div>
        <button
          type="button" onClick={onConfirm} disabled={!position}
          className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-all"
        >
          تأكيد الموقع
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   LocationField — text input + map-pin button. Typing enough text auto-
   opens the map and searches it live (no need to click the pin button
   first); clicking the pin button still opens it manually any time.
   Free typing without ever touching the map still works fine too — lat/lng
   just stay null until a location is actually picked/searched.
═══════════════════════════════════════════════════════════ */
const LocationField = ({ label, placeholder, value, onChange, lat, lng, onPick, inputClassName, labelClassName, autoCloseOnPick = false }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [position,   setPosition]   = useState(lat && lng ? [lat, lng] : null);
  const [locating,   setLocating]   = useState(false);
  const [geocoding,  setGeocoding]  = useState(false);
  const [searching,  setSearching]  = useState(false);

  // Prevents a reverse-geocode result (map click -> fills the text box)
  // from immediately re-triggering a forward search of that same text —
  // without this the two effects would ping-pong on every map click.
  const skipNextSearchRef = useRef(false);
  const searchTimerRef = useRef(null);
  useEffect(() => () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); }, []);

  const reverseGeocode = useCallback(async ([la, ln]) => {
    setGeocoding(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${la}&lon=${ln}&accept-language=ar`);
      const data = await res.json();
      const address = data.display_name || '';
      skipNextSearchRef.current = true;
      onChange(address);
      return address;
    } catch {
      /* leave the text box as whatever the user last typed */
      return null;
    } finally {
      setGeocoding(false);
    }
  }, [onChange]);

  // When autoCloseOnPick is set (نقل عام), directly interacting with the
  // map — a click, a marker drag, or "موقعي الحالي" — confirms and closes
  // the modal immediately once the address resolves, instead of requiring
  // a separate confirm-button tap. Typing a search query still requires
  // confirming manually, since a text search result can be ambiguous and
  // is worth a visual check before locking it in.
  const handleMapPick = async (pos) => {
    setPosition(pos);
    const address = await reverseGeocode(pos);
    if (autoCloseOnPick) {
      onPick(pos[0], pos[1], address || `${pos[0].toFixed(5)}, ${pos[1].toFixed(5)}`);
      setPickerOpen(false);
    }
  };

  const forwardGeocode = useCallback(async (query) => {
    const q = query.trim();
    if (q.length < AUTO_OPEN_MIN_CHARS) return;
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&countrycodes=sd&accept-language=ar`);
      const data = await res.json();
      if (data && data[0]) setPosition([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
    } catch {
      /* silent — the user can still confirm with the pin wherever it already is */
    } finally {
      setSearching(false);
    }
  }, []);

  const handleAddressChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (!pickerOpen && v.trim().length >= AUTO_OPEN_MIN_CHARS) setPickerOpen(true);
    if (skipNextSearchRef.current) { skipNextSearchRef.current = false; return; }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => forwardGeocode(v), SEARCH_DEBOUNCE_MS);
  };

  const handleAddressKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!pickerOpen) setPickerOpen(true);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    forwardGeocode(value);
  };

  const locateMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { handleMapPick([pos.coords.latitude, pos.coords.longitude]); setLocating(false); },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const confirm = () => {
    if (!position) return;
    onPick(position[0], position[1], value.trim() || `${position[0].toFixed(5)}, ${position[1].toFixed(5)}`);
    setPickerOpen(false);
  };

  return (
    <div>
      {label && <label className={labelClassName || 'block text-xs font-bold text-gray-600 mb-1.5'}>{label}</label>}
      <div className="flex gap-2">
        <input
          className={(inputClassName || 'w-full border-2 border-gray-200 hover:border-[#1E3A8A]/40 focus:border-[#1E3A8A] focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none') + ' flex-1'}
          placeholder={placeholder}
          value={value}
          onChange={handleAddressChange}
          onKeyDown={handleAddressKeyDown}
        />
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex-shrink-0 w-11 h-11 rounded-xl border-2 border-gray-200 hover:border-[#1E3A8A]/40 hover:bg-blue-50 flex items-center justify-center text-[#1E3A8A] transition-all"
          title="تحديد من الخريطة"
        >
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </button>
      </div>
      {lat && lng && <p className="text-[11px] text-emerald-600 mt-1">✓ تم تحديد الموقع من الخريطة</p>}

      {pickerOpen && (
        <LocationPickerModal
          title={label ? `تحديد: ${label}` : undefined}
          position={position}
          address={value}
          geocoding={geocoding}
          searching={searching}
          locating={locating}
          onAddressChange={handleAddressChange}
          onAddressKeyDown={handleAddressKeyDown}
          onMapClick={handleMapPick}
          onMarkerDrag={(e) => handleMapPick([e.target.getLatLng().lat, e.target.getLatLng().lng])}
          onLocateMe={locateMe}
          onConfirm={confirm}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default LocationField;
